import { ChevronDown } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { SimpleSelect, type SimpleSelectHandle } from '@/components/ui/simple-select';
import { cn } from '@/lib/utils';

interface NameOption {
  value: string;
  label: string;
}

interface ImportHistoryFiltersProps {
  isProperty: boolean;
  names: NameOption[];
  dateInFilter: string;
  showClearFilterBtn: boolean;
  orderStatusRef: React.RefObject<SimpleSelectHandle>;
  uploadByRef: React.RefObject<SimpleSelectHandle>;
  dateInputElement: React.RefObject<HTMLInputElement>;
  handleFilterChange: (key: string, value: string) => void;
  clearFilter: () => void;
}

export function ImportHistoryFilters({
  isProperty,
  names,
  dateInFilter,
  showClearFilterBtn,
  orderStatusRef,
  uploadByRef,
  dateInputElement,
  handleFilterChange,
  clearFilter,
}: ImportHistoryFiltersProps) {
  return (
    <form className="mb-4 flex flex-wrap items-center gap-3">
      <div className="w-full lg:w-[282px]">
        <Input
          id="Search-by-file-ID-file"
          name="Search-by-file-ID-file"
          type="search"
          onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
            handleFilterChange('fileName', e.target.value)
          }
          aria-describedby="Search-by-file-ID-file"
          placeholder="Search by File Name..."
          data-testid="Search-by-file-ID-file_id"
        />
      </div>

      <div className="relative w-full lg:w-[170px]">
        <Button
          variant="outline"
          type="button"
          aria-label="Uploaded Date"
          className={cn(
            'w-full justify-between font-normal',
            !dateInFilter && 'text-muted-foreground',
          )}
          onClick={() => dateInputElement.current?.showPicker()}
          data-testid="order-date"
        >
          {dateInFilter !== '' ? dateInFilter : 'Uploaded Date'}
          <ChevronDown className="ml-2 h-4 w-4" aria-hidden="true" />
        </Button>
        <Input
          ref={dateInputElement}
          name="uploadedDate"
          type="date"
          onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
            handleFilterChange('uploadedDate', e.target.value)
          }
          id="uploadedDate"
          aria-describedby="uploadedDate"
          data-testid="uploadedDate_id"
          className="invisible m-0 h-0 w-0 border-none p-0"
        />
      </div>

      <div className="w-full lg:w-[190px]">
        <SimpleSelect
          options={names}
          ref={uploadByRef}
          name="Upload-by"
          md="12"
          controlId="Upload-by"
          ariaDescribedBy="Upload-by"
          placeholder="Uploaded By"
          setFieldValue={handleFilterChange}
          field="uploadedBy"
        />
      </div>

      {!isProperty && (
        <div className="w-full lg:w-auto lg:min-w-[190px]">
          <SimpleSelect
            name="Order-status"
            md="12"
            options={[
              { value: 'placed', label: 'accepted/placed' },
              { value: 'rejected', label: 'Rejected' },
            ]}
            ref={orderStatusRef}
            controlId="Order-status"
            ariaDescribedBy="Order-status"
            placeholder="Order Status"
            setFieldValue={handleFilterChange}
            field="state"
          />
        </div>
      )}

      {showClearFilterBtn && (
        <Button type="reset" variant="link" onClick={clearFilter}>
          Clear Filters
        </Button>
      )}
    </form>
  );
}
